import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBackNavigation } from '../../../hooks/useBackNavigation';
import styles from './SapConnection.module.css';
import Card, { CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/Card/Card';
import { ConnectionConfig } from '../../../types/types';
import { useAuthStore } from '../../../store/authStore';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from '../../../lib/toast';
import ToggleSwitch from '../../../ui/ToggleSwitch/ToggleSwitch';
import SamlBearerSection from '../authSections/SamlBearerSection';
import OboJwtSection from '../authSections/OboJwtSection';
import { configApi } from '../../../lib/apiClient';
import BasicAuthSection from '../authSections/BasicAuthSection';
import OAuthClientCredentialsSection from '../authSections/OAuthClientCredentialsSection';
import ApiKeyAuthSection from '../authSections/ApiKeyAuthSection';
import DestinationSection from '../authSections/DestinationSection';
import PreviewSection from '../authSections/PreviewSection';
import { useConnectionsStore } from '../../../store/connectionsStore';
import { testConnectionApi } from '../../../lib/testConnection';
import SecureInput from '../../../ui/SecureInput/SecureInput';
import { Link as LinkIcon } from 'lucide-react';
import { UserRole } from '../../../enums/userRole';
import { hasMinRole } from '../../../lib/permissions';
import { Environment } from '../../../types/environment';

const configSchema = z.object({
    baseUrl: z.string().url('Invalid URL format'),
    name: z.string().optional().nullable(),
    environment: z.enum(['test', 'prod', 'dev']).optional(),
    authType: z.enum(['none', 'api_key', 'basic', 'oauth2_client_credentials', 'oauth2_obo_jwt', 'saml2_bearer', 'btp_destination']),
    csrfEnabled: z.boolean().default(false),
    // oauth2 client credentials
    basic: z
        .object({
            username: z.string().optional().nullable(),
            password: z.string().optional().nullable(),
        })
        .optional()
        .default({ username: '', password: '' }),
    oauth: z
        .object({
            token_url: z.string().url().optional().nullable().or(z.literal('')),
            client_id: z.string().optional().nullable(),
            client_secret: z.string().optional().nullable(),
            scope: z.string().optional().nullable(),
        })
        .optional()
        .default({ token_url: '', client_id: '', client_secret: '', scope: '' }),
    // oauth2 OBO JWT
    obo: z
        .object({
            token_url: z.string().optional().nullable(),
            audience: z.string().optional().nullable(),
            resource: z.string().optional().nullable(),
            issuer_discovery: z.string().optional().nullable(),
        })
        .optional()
        .default({ token_url: '', audience: '', resource: '', issuer_discovery: '' }),
    // saml2 bearer
    saml: z
        .object({
            token_url: z.string().optional().nullable(),
            issuer: z.string().optional().nullable(),
            audience: z.string().optional().nullable(),
            private_key: z.string().optional().nullable(),
            certificate: z.string().optional().nullable(),
            client_id: z.string().optional().nullable(),
            scope: z.string().optional().nullable(),
            client_username: z.string().optional().nullable(),
            client_password: z.string().optional().nullable(),
            testNameId: z.string().optional().nullable(),
        })
        .optional()
        .default({ token_url: '', issuer: '', audience: '', private_key: '', certificate: '', client_id: '', scope: '', client_username: '', client_password: '', testNameId: '' }),
    // api key
    apikey: z
        .object({
            key_name: z.string().optional().nullable(),
            key_value: z.string().optional().nullable(),
            in: z.enum(['header', 'query']).optional().default('header'),
        })
        .optional()
        .default({ key_name: '', key_value: '', in: 'header' }),
    // btp destination
    destination: z
        .object({
            name: z.string().optional().nullable(),
            subaccount: z.string().optional().nullable(),
            auth: z.enum(['OAuth2ClientCredentials', 'OAuth2UserTokenExchange', 'OAuth2SAMLBearerAssertion']).optional().nullable(),
            forward_auth_token: z.boolean().optional(),
        })
        .optional()
        .default({ name: '', subaccount: '', auth: 'OAuth2ClientCredentials', forward_auth_token: false }),
    // advanced
    advanced: z
        .object({
            via_cloud_connector: z.boolean().optional(),
            location_id: z.string().optional().nullable(),
            proxy_url: z.string().optional().nullable(),
            test_path: z.string().optional().nullable(),
            allowSelfSignedCertificates: z.boolean().optional().default(false),
        })
        .default({ via_cloud_connector: false, location_id: '', proxy_url: '', test_path: '', allowSelfSignedCertificates: false }),
})


type ConfigForm = z.infer<typeof configSchema>

const SapConnection: React.FC = ({ }) => {
    const navigate = useNavigate();
    const goBack = useBackNavigation('/connectors');
    const [config, setConfig] = useState<ConnectionConfig | null>(null)
    const [saving, setSaving] = useState(false)
    const [testing, setTesting] = useState(false)
    const [customHeaders, setCustomHeaders] = useState<Array<{ key: string, value: string }>>([])
    const user = useAuthStore(state => state.user)
    const { connectors, updateConnection } = useConnectionsStore(state => state);
    const { configId } = useParams();

    const {
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
        setError,
    } = useForm<ConfigForm>({
        resolver: zodResolver(configSchema),
        defaultValues: {
            authType: 'basic',
            name: '',
            csrfEnabled: false,
            basic: { username: '', password: '' },
            oauth: { token_url: '', client_id: '', client_secret: '', scope: '' },
            obo: { token_url: '', audience: '', resource: '', issuer_discovery: '' },
            saml: { token_url: '', issuer: '', audience: '', private_key: '', certificate: '', client_id: '', scope: '', client_username: '', client_password: '', testNameId: '' },
            apikey: { key_name: '', key_value: '', in: 'header' },
            destination: { name: '', subaccount: '', auth: 'OAuth2ClientCredentials', forward_auth_token: false },
            advanced: { via_cloud_connector: false, location_id: '', proxy_url: '', test_path: '/sap/opu/odata/sap/API_BUSINESS_PARTNER/A_BusinessPartner?$top=1', allowSelfSignedCertificates: false },
        }
    })
    const authType = watch('authType') || 'basic'

    useEffect(() => {
        loadConfig()
    }, [connectors, configId])

    useEffect(() => {
        if (!user || !hasMinRole(user?.role?.id, UserRole.Admin)) {
            navigate('/');
        }
    }, [user])

    const loadConfig = async () => {
        try {
            const configData = connectors.find(c => c.id === configId) || null;
            if (configData) {
                setConfig(configData)

                // Map backend auth_fields to form fields
                const formValues: Partial<ConfigForm> = {
                    baseUrl: configData.config.baseUrl,
                    name: configData.connectionName,
                    environment: configData.environment as Environment | undefined,
                    csrfEnabled: configData.config.csrfEnabled,
                    authType: ['none', 'api_key', 'basic', 'oauth2_client_credentials', 'oauth2_obo_jwt', 'saml2_bearer', 'btp_destination'].includes(configData.authType)
                        ? (configData.authType as ConfigForm['authType'])
                        : 'basic',
                    basic: configData.authFields?.basic || { username: '', password: '' },
                    oauth: configData.authFields?.oauth || { token_url: '', client_id: '', client_secret: '', scope: '' },
                    obo: configData.authFields?.obo || { token_url: '', audience: '', resource: '', issuer_discovery: '' },
                    saml: configData.authFields?.saml || { token_url: '', issuer: '', audience: '', private_key: '', certificate: '', client_id: '', scope: '', client_username: '', client_password: '', testNameId: '' },
                    apikey: configData.authFields?.apikey || { key_name: '', key_value: '', in: 'header' },
                    destination: configData.authFields?.destination || { name: '', subaccount: '', auth: 'OAuth2ClientCredentials', forward_auth_token: false },
                    advanced: {
                        proxy_url: configData.httpProxy || '',
                        via_cloud_connector: configData.config.cloudProvider || false,
                        test_path: configData.config.testPath || '',
                        location_id: '',
                        allowSelfSignedCertificates: configData.config.allowSelfSignedCertificates || false,
                    },
                }

                Object.entries(formValues).forEach(([key, value]) => {
                    if (key in configSchema.shape) {
                        setValue(key as keyof ConfigForm, value as any)
                    }
                })

                const headers = Object.entries(configData.defaultHeaders || {})
                    .map(([key, value]) => ({ key: key as string, value: value as string }))
                setCustomHeaders(headers)
            }
        } catch (error: any) {
            toast.error(`Failed to load configuration: ${error.message}`)
        } finally {
            // setLoading(false)
        }
    }

    const onSubmit = async (data: ConfigForm) => {
        if (!data.environment) {
            setError('environment', { type: 'manual', message: 'Environment is required' });
            toast.error('Please select an environment');
            return;
        }

        setSaving(true)
        try {
            const headersObj = customHeaders.reduce((acc, { key, value }) => {
                if (key.trim() && value.trim()) {
                    acc[key] = value
                }
                return acc
            }, {} as Record<string, string>)

            // Build auth_fields object
            const authFields = {
                basic: data.basic,
                oauth: data.oauth,
                obo: data.obo,
                saml: data.saml,
                destination: data.destination,
                apikey: data.apikey,
            }

            // Build config object per new backend structure
            const configPayload = {
                baseUrl: data.baseUrl,
                csrfEnabled: data.csrfEnabled,
                cloudProvider: data.advanced?.via_cloud_connector,
                testPath: data.advanced?.test_path,
                allowSelfSignedCertificates: data.advanced?.allowSelfSignedCertificates ?? false,
            }

            // Final payload aligned with ConnectionConfigCreateDto
            const payload = {
                connectionName: data.name || '',
                environment: data.environment || undefined,
                authType: data.authType,
                config: configPayload,
                authFields,
                httpProxy: data.advanced?.proxy_url,
                defaultHeaders: headersObj,
                userId: user?.id!,
            }

            let response;
            if (!config) response = await configApi.post(payload)
            else response = await configApi.put(config.id, payload)

            setConfig(response)
            updateConnection(response)
            toast.success('Configuration saved successfully!')
        } catch (error: any) {
            toast.error(`Failed to save configuration: ${error.message}`)
        } finally {
            setSaving(false)
        }
    }

    const testConnection = async () => {
        if (!config?.id) {
            toast.error('Please save configuration first');
            return;
        }

        // Get current form values
        const formData = watch();

        // Build connection object from current form data
        const headersObj = customHeaders.reduce((acc, { key, value }) => {
            if (key.trim() && value.trim()) {
                acc[key] = value
            }
            return acc
        }, {} as Record<string, string>)

        const authFields = {
            basic: formData.basic,
            oauth: formData.oauth,
            obo: formData.obo,
            saml: formData.saml,
            destination: formData.destination,
            apikey: formData.apikey,
        }

        const configPayload = {
            baseUrl: formData.baseUrl,
            csrfEnabled: formData.csrfEnabled,
            cloudProvider: formData.advanced?.via_cloud_connector,
            testPath: formData.advanced?.test_path,
            allowSelfSignedCertificates: formData.advanced?.allowSelfSignedCertificates ?? false,
        }

        const connectionData = {
            connectionName: formData.name || '',
            environment: formData.environment || undefined,
            authType: formData.authType,
            config: configPayload,
            authFields,
            httpProxy: formData.advanced?.proxy_url,
            defaultHeaders: headersObj,
        }

        await testConnectionApi(config.id, setTesting, connectionData, user);
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h1>SAP Connection</h1>
                    <p>Configure your SAP system connection settings</p>
                </div>
                <div className={styles.headerActions}>
                    <div className={styles.topButtons}>
                        <button
                            type="button"
                            onClick={goBack}
                            className={styles.cancelBtn}
                        >
                            Back
                        </button>
                        <button
                            type="button"
                            onClick={testConnection}
                            disabled={testing || !config}
                            className={styles.saveBtnTop}
                        >
                            {testing ? 'Testing...' : 'Test Connection'}
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit(onSubmit)}
                            disabled={saving}
                            className={styles.saveBtnTop}
                        >
                            {saving ? 'Saving...' : 'Save Configuration'}
                        </button>
                    </div>
                    {/* {testResult && (
                        <div className={styles.testResult}>
                            <p>Connection Test Result: </p>
                            {/* <div className={`${styles.resultCard} `}>
                                <div className={`${styles.resultStatus} ${testResult.success ? styles.success : styles.error}`}>
                                    {testResult.success ? '   Success' : ' Failed'}
                                </div>
                                <div className={styles.resultMessage}>{testResult.message}</div>
                            {/* </div>
                            <div className={styles.closeButton} onClick={() => setTestResult(null)}> × </div>
                        </div>
                    )} */}
                </div>

            </div>
            {/* TEST RESULT */}

            <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
                {/* ------------------------------------------------------ */}
                {/* BASIC SETTINGS                                          */}
                {/* ------------------------------------------------------ */}
                <Card className={styles.section}>
                    <CardHeader>
                        <CardTitle>Basic Settings</CardTitle>
                        <CardDescription>Core endpoint configuration used by all auth types.</CardDescription>
                    </CardHeader>

                    <CardContent>

                        <div className={styles.field}>
                            <SecureInput
                                fieldType="name"
                                label="Connection Name"
                                value={watch('name') || ''}
                                onChange={(value) => setValue('name', value)}
                                className={styles.input}
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label}>Environment</label>
                            <select
                                value={watch('environment') || ''}
                                onChange={(e) => {
                                    setValue('environment', e.target.value as 'test' | 'prod' | 'dev');
                                    setError('environment', { type: 'manual', message: '' });
                                }}
                                className={`${styles.select} ${errors.environment ? styles.selectError : ''}`}
                            >
                                <option value="">Select environment</option>
                                <option value="prod">Prod</option>
                                <option value="test">Test</option>
                                <option value="dev">Dev</option>
                            </select>
                            {errors.environment && (
                                <span className={styles.error}>{errors.environment.message}</span>
                            )}
                        </div>
                        <div className={styles.field}>
                            <SecureInput
                                fieldType="url"
                                label="SAP Base URL *"
                                icon={<LinkIcon size={16} />}
                                value={watch('baseUrl') || ''}
                                onChange={(value) => setValue('baseUrl', value)}
                                required
                                className={styles.input}
                                externalError={errors.baseUrl?.message}
                            />
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label}>Authentication Type *</label>
                            <select
                                value={watch('authType')}
                                onChange={(e) => setValue('authType', e.target.value as any)}
                                className={`${styles.select} ${errors.authType ? styles.selectError : ''}`}
                            >
                                <option value="none">No Authentication</option>
                                <option value="api_key">API Key</option>
                                <optgroup label="Standard">
                                    <option value="oauth2_client_credentials">OAuth2 Client Credentials</option>
                                    <option value="basic">Basic Authentication</option>
                                </optgroup>
                                <optgroup label="Principal Propagation">
                                    <option value="oauth2_obo_jwt">OAuth2 On-Behalf-Of (JWT Bearer)</option>
                                    <option value="saml2_bearer">SAML2 Bearer Assertion</option>
                                    <option value="btp_destination">SAP BTP Destination (ForwardAuthToken)</option>
                                </optgroup>
                            </select>
                            {errors.authType && (
                                <span className={styles.error}>{errors.authType.message}</span>
                            )}
                        </div>

                    </CardContent>
                </Card>

                {/* ------------------------------------------------------ */}
                {/* API KEY                                                 */}
                {/* ------------------------------------------------------ */}
                {authType === 'api_key' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>API Key</CardTitle>
                            <CardDescription>Send an API key as a header or query parameter.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <ApiKeyAuthSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* ------------------------------------------------------ */}
                {/* BASIC AUTH                                              */}
                {/* ------------------------------------------------------ */}
                {authType === 'basic' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>Basic Authentication</CardTitle>
                            <CardDescription>Use only for legacy systems and development.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <BasicAuthSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* ------------------------------------------------------ */}
                {/* OAUTH2 CLIENT CREDENTIALS                               */}
                {/* ------------------------------------------------------ */}
                {authType === 'oauth2_client_credentials' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>OAuth2 Client Credentials</CardTitle>
                            <CardDescription>Technical client for calling SAP APIs.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <OAuthClientCredentialsSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* ------------------------------------------------------ */}
                {/* OAUTH2 OBO                                              */}
                {/* ------------------------------------------------------ */}
                {authType === 'oauth2_obo_jwt' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>OAuth2 On-Behalf-Of (JWT Bearer)</CardTitle>
                            <CardDescription>Exchange IAS token for ABAP access token.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <OboJwtSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* ------------------------------------------------------ */}
                {/* SAML2 BEARER                                            */}
                {/* ------------------------------------------------------ */}
                {authType === 'saml2_bearer' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>SAML2 Bearer Assertion</CardTitle>
                            <CardDescription>Exchange a signed SAML assertion for access token.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <SamlBearerSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* ------------------------------------------------------ */}
                {/* BTP DESTINATION                                         */}
                {/* ------------------------------------------------------ */}
                {authType === 'btp_destination' && (
                    <Card className={styles.section}>
                        <CardHeader>
                            <CardTitle>SAP BTP Destination</CardTitle>
                            <CardDescription>Call via Destination service; optionally forward user token.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <DestinationSection form={{ watch, setValue }} styles={styles} />
                        </CardContent>
                    </Card>
                )}

                {/* ------------------------------------------------------ */}
                {/* ADVANCED SETTINGS                                       */}
                {/* ------------------------------------------------------ */}
                <Card className={styles.section}>
                    <CardHeader>
                        <CardTitle>Advanced Settings</CardTitle>
                    </CardHeader>

                    <CardContent className={styles.content}>

                        <div className={styles.fieldAdvanced}>
                            <label className={styles.label}>Enable CSRF Token Flow</label>
                            <div className={styles.checkboxLabel}>
                                <ToggleSwitch
                                    checked={watch('csrfEnabled')}
                                    onChange={(checked) => setValue('csrfEnabled', checked)}
                                />
                            </div>
                        </div>

                        <div className={styles.field}>
                            <div className={styles.fieldAdvanced}>
                                <label className={styles.label}>Allow Self-Signed Certificates</label>
                                <div className={styles.checkboxLabel}>
                                    <ToggleSwitch
                                        checked={!!watch('advanced.allowSelfSignedCertificates')}
                                        onChange={(checked) => setValue('advanced.allowSelfSignedCertificates', checked)}
                                    />
                                </div>
                            </div>
                            <p style={{ fontSize: '12px', color: '#e67e22', marginTop: '4px' }}>
                                ⚠ Disables SSL certificate verification. Use only for internal or development SAP systems with self-signed certificates.
                            </p>
                        </div>

                    </CardContent>
                </Card>

                {/* PREVIEW TABS SECTION */}
                <PreviewSection
                    styles={styles}
                    authType={authType}
                    testConnection={testConnection}
                    testing={testing}
                    config={config}
                    form={{ watch, setValue }}
                />


            </form>
        </div>
    );
};

export default SapConnection;
